import { QueryClient } from '@tanstack/react-query'

import type { Any } from '@api/orvalFetcher'
import { socketLog } from '@features/websocket/socketService'

type CollectionType = Array<{ [key: string]: Any }>
type PagesType = Array<{ collection: CollectionType; meta: Any }>
type DataType = { pages: PagesType; pageParams: Array<Any> }
type KeysType = Array<string>
type EntryType = [KeysType, DataType]
type EntriesType = Array<EntryType>

type UpdateType = 'updated' | 'cancelled'
type DeleteType = 'deleted'
type RiskScoreDoNotCreateNewRowType = 'riskScore_do_not_create_new_row'
export type Events = UpdateType | DeleteType | RiskScoreDoNotCreateNewRowType | 'created' | 'message'
export function updateRowInQueries(
  qc: QueryClient,
  baseKey: readonly unknown[],
  updatedRow: Record<string, Any>,
  opts: { idKey?: string } = {},
  payloadEvent: Events,
  isFiltersEnabled = false,
) {
  const idKey = opts.idKey ?? 'id'
  const entries = qc.getQueriesData({ queryKey: baseKey }) as EntriesType

  if (!entries || entries.length === 0) return

  const newEntries = entries.reduce((acc: EntriesType, [qKey, data]: EntryType): EntriesType => {
    if (data?.pages && Array.isArray(data.pages)) {
      const pages = data.pages

      let isFound = false
      const newPages = pages.map((page) => {
        const collection = page?.collection || []

        const newCollection = collection.reduce((rowAcc: CollectionType, row) => {
          const isRowFound = row?.[idKey] === updatedRow?.[idKey]

          if (isRowFound) {
            isFound = true

            socketLog(`updateRowInQueries: "${updatedRow?.[idKey]}" found in the collection, event: ${payloadEvent}`)

            const isUpdatedEvent =
              payloadEvent === 'updated' ||
              payloadEvent === 'cancelled' ||
              payloadEvent === 'created' ||
              payloadEvent === 'message' ||
              payloadEvent === 'riskScore_do_not_create_new_row'

            if (isUpdatedEvent) {
              rowAcc.push({ ...row, ...updatedRow })
            }

            const isDeletedEvent = payloadEvent === 'deleted'
            if (isDeletedEvent) {
              return rowAcc
            }
          } else {
            // Default - add existing row
            rowAcc.push(row)
          }

          return rowAcc
        }, [])

        return { ...page, collection: newCollection }
      }) as PagesType

      if (!isFound) {
        socketLog(
          `updateRowInQueries: "${updatedRow?.[idKey]}" NOT found in the collection, Assume that it was an "add" operation: ${payloadEvent}`,
        )

        if (newPages[0]?.collection) {
          if (isFiltersEnabled) {
            socketLog('UpdateRowInQueries: isFiltersEnabled is true, skipping adding to collection')
          } else if (payloadEvent === 'riskScore_do_not_create_new_row') {
            socketLog('UpdateRowInQueries: riskScore_do_not_create_new_row, skipping adding to collection')
          } else {
            newPages[0].collection = [updatedRow, ...(newPages[0]?.collection ?? [])]
          }
        } else {
          socketLog('updateRowInQueries: newPages[0]?.collection is not defined')
        }
      }

      acc.push([qKey, { ...data, pages: newPages as PagesType }])
    }

    return acc
  }, [])

  newEntries.forEach(([qKey, data]) => {
    qc.setQueryData(qKey, data)
  })

  return
}

/**
 * This function replaces the entire collection at once. Used when payload data is returned in bulk with no ids
 * (e.g. failed rows).
 */
export const replaceCollectionInQueries = (
  qc: QueryClient,
  baseKey: readonly unknown[],
  rows: Record<string, unknown>[],
) => {
  const entries = qc.getQueriesData({ queryKey: baseKey }) as EntriesType

  if (!entries || entries.length === 0) return

  entries.forEach(([qKey, data]: EntryType) => {
    if (data?.pages && Array.isArray(data.pages)) {
      const newPages = data.pages.map((page, i) =>
        // Put all rows in the first page, clear the rest
        i === 0 ? { ...page, collection: rows } : { ...page, collection: [] },
      )
      qc.setQueryData(qKey, { ...data, pages: newPages })
    }
  })
}
